import { z } from 'zod';
import { BUILT_IN_BADGE_LAYOUT, badgeLayoutSchema } from './badge-layout.js';
import { accentColorSchema } from './kiosk-branding.js';
/**
 * The closed list of label stock (BDG-02). Media is CODE, not free-form dimensions:
 * an arbitrary size cannot be loaded into a real printer.
 * Dimensions are verbatim from docs/plans/badge-printing.md §"Media table (300 dpi)".
 */
export const BADGE_MEDIA_IDS = [
    'W62X100_DIE_CUT',
    'W62_CONTINUOUS',
    'W62_CONTINUOUS_2COLOR',
];
export const BADGE_MEDIA_KINDS = ['CONTINUOUS', 'DIE_CUT'];
export const BADGE_MEDIA = {
    W62X100_DIE_CUT: { id: 'W62X100_DIE_CUT', kind: 'DIE_CUT', widthMm: 62, lengthMm: 100, printableDotsWidth: 696, printableDotsHeight: 1109, colorPlanes: 1, leftMarginPins: 12 },
    W62_CONTINUOUS: { id: 'W62_CONTINUOUS', kind: 'CONTINUOUS', widthMm: 62, lengthMm: 100, printableDotsWidth: 696, printableDotsHeight: 1109, colorPlanes: 1, leftMarginPins: 12 },
    W62_CONTINUOUS_2COLOR: { id: 'W62_CONTINUOUS_2COLOR', kind: 'CONTINUOUS', widthMm: 62, lengthMm: 100, printableDotsWidth: 696, printableDotsHeight: 1109, colorPlanes: 2, leftMarginPins: 12 },
};
// DO NOT "correct" the die-cut heights to 341 / 1061. Brother publishes TOTAL and
// PRINTABLE dots side by side and they are easy to swap: 62x29mm is 732x341 total but
// 696x271 printable; 29x90mm is 342x1061 total but 306x991 printable. Layout uses the
// printable figure — the total overruns the label and the excess is silently not
// printed, costing the badge its bottom rows. Verified against Brother's Raster
// Command Reference and brother_ql's labels.py. See docs/plans/badge-printing.md.
/** A stored template's scope (BDG-01, BDG-08). Exactly one of the four. */
export const BADGE_TEMPLATE_SCOPES = [
    'TENANT',
    'LOCATION',
    'VISITOR_TYPE',
    'LOCATION_VISITOR_TYPE',
];
/** Where a resolved template came from. `BUILT_IN` is the bottom rung and has no row. */
export const BADGE_TEMPLATE_SOURCES = [
    'LOCATION_VISITOR_TYPE',
    'VISITOR_TYPE',
    'LOCATION',
    'TENANT',
    'BUILT_IN',
];
export const BADGE_CUSTOM_LINE_MAX_LENGTH = 64;
export const BADGE_VALIDITY_MINUTES_MIN = 15;
export const BADGE_VALIDITY_MINUTES_MAX = 1440;
/**
 * `PUT /api/web/visitor/badge-templates` request body (BDG-09 — `.strict()`).
 *
 * `logoFileId` is DELIBERATELY ABSENT. The column exists on the model from
 * S35-TASK-002 and resolution honours it (S35-TASK-003), but nothing renders a logo
 * until S35 Phase 2, and there is no badge-logo upload route yet. `.strict()` therefore
 * answers 400 to a payload carrying it, which is the honest answer today and is
 * asserted as a negative test below. Phase 2 adds the field here together with the
 * upload route and the renderer that consumes it.
 */
export const upsertBadgeTemplateSchema = z
    .object({
    scope: z.enum(BADGE_TEMPLATE_SCOPES),
    locationId: z.string().min(1).nullable(),
    visitorTypeId: z.string().min(1).nullable(),
    media: z.enum(BADGE_MEDIA_IDS),
    /** Required (S35-TASK-012). The eleven-toggle `fields` shape it superseded is gone
     *  (S35-TASK-014): a badge is composed of zones and slots, not independent toggles. */
    layout: badgeLayoutSchema,
    accentColor: accentColorSchema.nullable(),
    customLineText: z.string().trim().min(1).max(BADGE_CUSTOM_LINE_MAX_LENGTH).nullable(),
    validityMinutes: z
        .number()
        .int()
        .min(BADGE_VALIDITY_MINUTES_MIN)
        .max(BADGE_VALIDITY_MINUTES_MAX)
        .nullable(),
})
    .strict()
    .superRefine((value, ctx) => {
    const expected = {
        TENANT: { locationId: false, visitorTypeId: false },
        LOCATION: { locationId: true, visitorTypeId: false },
        VISITOR_TYPE: { locationId: false, visitorTypeId: true },
        LOCATION_VISITOR_TYPE: { locationId: true, visitorTypeId: true },
    };
    const want = expected[value.scope];
    if (want.locationId !== (value.locationId !== null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['locationId'], message: 'SCOPE_MISMATCH' });
    }
    if (want.visitorTypeId !== (value.visitorTypeId !== null)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['visitorTypeId'], message: 'SCOPE_MISMATCH' });
    }
});
/** `GET /api/web/visitor/badge-templates/effective` query. */
export const effectiveBadgeTemplateQuerySchema = z
    .object({
    locationId: z.string().min(1).optional(),
    visitorTypeId: z.string().min(1).optional(),
})
    .strict();
/**
 * `GET /api/web/visitor/badge-templates/preview` query (S35-TASK-017, PRD-009 RND-02).
 * Shaped identically to `effectiveBadgeTemplateQuerySchema` — the preview resolves the
 * SAME ladder the effective-template route resolves, against sample visit data instead of
 * a real one. Both fields are a CLIENT REQUEST, not an authorization decision: the route
 * intersects them with the caller's own location scope before either reaches a `WHERE`.
 */
export const badgePreviewQuerySchema = z
    .object({
    locationId: z.string().min(1).optional(),
    visitorTypeId: z.string().min(1).optional(),
})
    .strict();
/**
 * `POST /api/web/visitor/badge-templates/preview` body — the LIVE preview of an UNSAVED
 * DRAFT (PRD-009 RND-02, the same sample-data rendering the `GET` above performs).
 *
 * THE DIFFERENCE FROM THE `GET` IS *WHERE THE TEMPLATE COMES FROM*, and nothing else. The
 * `GET` renders the template the ladder RESOLVES FROM STORED ROWS; this renders the one the
 * admin currently has on screen and has not saved, so the Badges page can show the effect of
 * a media, layout, accent, custom line or validity change before — and without — a `PUT`.
 * Every other rule the `GET` records still holds: sample visit data, never a real visit; an
 * SVG document, never the `{ data }` envelope; `no-store`.
 *
 * `locationId` and `visitorTypeId` are a CLIENT REQUEST, NEVER AN AUTHORIZATION DECISION —
 * verbatim the rule `badgePreviewQuerySchema` records for its own two fields. The route
 * intersects them with the caller's own location scope (a `locationId` outside it is a 404)
 * before either reaches a `WHERE`. They are `.nullable()` rather than `.optional()` here
 * because a draft always HAS a scope selected in the form, and "the tenant baseline" is an
 * explicit `null` rather than an absent key — a body is not a query string, and a form that
 * cleared its location selection must say so rather than omit it.
 *
 * `logoFileId` IS DELIBERATELY ABSENT, for a different reason than
 * `upsertBadgeTemplateSchema`'s: the logo is not a draftable field at all. It has no control
 * in the form, it is uploaded separately, and it still resolves from the STORED ladder — so
 * the route reads the resolved `logoFileId` from the effective template even while rendering
 * an unsaved draft. A draft carrying its own logo id would be a second, unvalidated path to
 * an internal `StoredFile` key (docs/008 S7).
 *
 * `customLineText` is `.max()` WITHOUT the `.trim().min(1)` the upsert schema applies: the
 * saved value may not be an empty string, but a draft mid-edit legitimately is one, and
 * rejecting it would blank the preview the instant an admin selects the text to retype it.
 * The stored bound is the one that matters, and it is the same constant. `validityMinutes`
 * keeps the upsert's bounds exactly — a value the admin cannot save is not worth previewing.
 */
export const badgeDraftPreviewSchema = z
    .object({
    media: z.enum(BADGE_MEDIA_IDS),
    layout: badgeLayoutSchema,
    accentColor: accentColorSchema.nullable(),
    customLineText: z.string().max(BADGE_CUSTOM_LINE_MAX_LENGTH).nullable(),
    validityMinutes: z
        .number()
        .int()
        .min(BADGE_VALIDITY_MINUTES_MIN)
        .max(BADGE_VALIDITY_MINUTES_MAX)
        .nullable(),
    locationId: z.string().min(1).nullable(),
    visitorTypeId: z.string().min(1).nullable(),
})
    .strict();
/**
 * A STORED template row on the wire.
 *
 * DELIBERATELY ABSENT (docs/008 S7, the same rule `kioskBrandingValuesSchema` records):
 * `tenantId` (the token already decided it) and `logoFileId` (an internal `StoredFile`
 * key). `hasCustomLogo` is the boolean a client actually needs.
 */
export const badgeTemplateDTOSchema = z.object({
    id: z.string().min(1),
    scope: z.enum(BADGE_TEMPLATE_SCOPES),
    locationId: z.string().min(1).nullable(),
    visitorTypeId: z.string().min(1).nullable(),
    media: z.enum(BADGE_MEDIA_IDS),
    layout: badgeLayoutSchema,
    accentColor: accentColorSchema.nullable(),
    hasCustomLogo: z.boolean(),
    customLineText: z.string().max(BADGE_CUSTOM_LINE_MAX_LENGTH).nullable(),
    validityMinutes: z.number().int().nullable(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
});
/** Where the resolved accent colour came from, so a console can say "inherited". */
export const BADGE_ACCENT_SOURCES = ['TEMPLATE', 'KIOSK_BRANDING', 'NONE'];
/**
 * One rung of the resolution ladder, as reported back to a caller so a console can render
 * "location + visitor type -> visitor type -> location -> tenant -> built-in" with which
 * rungs are actually defined. `scope` reuses `BadgeTemplateSource` — a rung is either one
 * of the four stored scopes or the rowless `BUILT_IN` bottom rung, the same union that
 * already names a *winning* source. `isDefined` is whether a row exists at this rung at
 * all, independent of whether it WON — a rung below the winner can still be
 * `isDefined: true`.
 */
export const badgeTemplateLadderRungDTOSchema = z.object({
    scope: z.enum(BADGE_TEMPLATE_SOURCES),
    isDefined: z.boolean(),
    /** The stored row's id when `isDefined`, else `null`. `BUILT_IN` is always `null`. */
    templateId: z.string().min(1).nullable(),
});
/**
 * The whole ladder behind one resolution (BDG-01, BDG-08): every rung, ordered
 * highest-precedence first — `LOCATION_VISITOR_TYPE`, `VISITOR_TYPE`, `LOCATION`,
 * `TENANT`, `BUILT_IN` — plus which one won. Lets a console show the full chain, not just
 * the winner, so an admin can see why a badge resolved the way it did.
 */
export const badgeTemplateResolutionDTOSchema = z.object({
    winning: z.enum(BADGE_TEMPLATE_SOURCES),
    rungs: z.tuple([
        badgeTemplateLadderRungDTOSchema,
        badgeTemplateLadderRungDTOSchema,
        badgeTemplateLadderRungDTOSchema,
        badgeTemplateLadderRungDTOSchema,
        badgeTemplateLadderRungDTOSchema,
    ]),
});
/** `GET /api/web/visitor/badge-templates/coverage` query. */
export const badgeTemplateCoverageQuerySchema = z
    .object({
    locationId: z.string().min(1).optional(),
})
    .strict();
/**
 * `GET /api/web/visitor/badge-templates/coverage` response: the visitor types that have
 * their OWN `LOCATION_VISITOR_TYPE` template at the given location (or, with no
 * `locationId`, their own org-wide `VISITOR_TYPE` template) — what a console needs to
 * render "covered / not covered" per visitor type without resolving each one individually.
 */
export const badgeTemplateCoverageDTOSchema = z.object({
    visitorTypeIds: z.array(z.string().min(1)),
});
/** `GET /api/web/visitor/badge-templates/effective` response. */
export const effectiveBadgeTemplateDTOSchema = z.object({
    source: z.enum(BADGE_TEMPLATE_SOURCES),
    /** The stored row this resolved from, or `null` when `source` is `BUILT_IN`. */
    templateId: z.string().min(1).nullable(),
    locationId: z.string().min(1).nullable(),
    visitorTypeId: z.string().min(1).nullable(),
    media: z.enum(BADGE_MEDIA_IDS),
    layout: badgeLayoutSchema,
    accentColor: accentColorSchema.nullable(),
    accentColorSource: z.enum(BADGE_ACCENT_SOURCES),
    hasCustomLogo: z.boolean(),
    customLineText: z.string().max(BADGE_CUSTOM_LINE_MAX_LENGTH).nullable(),
    validityMinutes: z.number().int().nullable(),
    /** The full ladder behind `source`/`templateId` above (BDG-01, BDG-08). */
    resolution: badgeTemplateResolutionDTOSchema,
});
/**
 * The bottom rung of the resolution ladder (BDG-01): a COMPLETE template, never a null.
 *
 * The enabled set deliberately mirrors what the S13 browser badge already shows
 * (`apps/visitor/src/components/visit-badge.tsx`: visitor, host, location, date, photo)
 * plus the logo, so a tenant that configures nothing gets continuity rather than a
 * surprise. `W62X100_DIE_CUT` (62 x 100 mm die-cut) is the default because it is the
 * only supported die-cut stock shaped like a badge.
 */
export const BUILT_IN_BADGE_TEMPLATE = {
    media: 'W62X100_DIE_CUT',
    layout: BUILT_IN_BADGE_LAYOUT,
    accentColor: null,
    customLineText: null,
    validityMinutes: null,
};
//# sourceMappingURL=badge-template.js.map